import { Color, Group, Mesh, MeshStandardMaterial, Object3D, Vector3 } from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

import { getPositionArray } from '../../../../utils/three/buffer-attribute';
import { getMaterials } from '../../../../utils/three/material';
import { isDefined } from '../../../../utils/variables';
import { CoordinateManager } from '../../../coordinate-manager';
import { clamp, toVector3 } from '../../../utilities/math-helpers';
import { disposeRecursive } from '../../../utilities/three/dispose';

type SkyMapPrismVolumeExtras = {
  skyMapPrismVolume?: {
    version: '1.0';
    cut: number;
    fill: number;
    cutAndFillData: number[];
    area: number[];
    planArea: number[];
  };
};

/**
 *  Builds and manages the volume model.
 */
export class PrismVolumeModel extends Object3D {
  projectPosition = new Vector3(0, 0, 0);

  private _opacity = 1;

  get opacity() {
    return this._opacity;
  }

  set opacity(value: number) {
    this._opacity = clamp(value, 0, 1);
    this.traverse((object3d) => {
      for (const material of getMaterials(object3d)) {
        material.transparent = this._opacity < 1;
        material.opacity = this._opacity;
      }
    });
  }

  /**
   *  Setter and getters for the fill volume model
   */
  get fillVisibleState() {
    return this.model.visible;
  }

  set fillVisibleState(state: boolean) {
    this.model.visible = state;
  }

  get gltfModel() {
    return this.model;
  }

  /**
   *  Setter and getters for the cut volume model
   */
  get cutVisibleState() {
    return this.model.visible;
  }

  set cutVisibleState(state: boolean) {
    this.model.visible = state;
  }

  get planarAreaCut() {
    return 0;
  }

  get planarAreaFill() {
    return 0;
  }

  /**
   *  Setter and getter for the models raised state.
   */
  set raised(state: boolean) {
    this.isRaised = state;
    this.model.position.set(0, 0, this.isRaised ? this.raisedHeight : 0);
  }

  get raised() {
    return this.isRaised;
  }

  /** How far the model is raised, in world units. */
  get raise() {
    return this.isRaised ? this.raisedHeight : 0;
  }

  set displayGradient(_state: boolean) {
    if (!this.containsHeightData) {
      return;
    }
  }

  /**
   * Same behaviour as "calculateLocalPosition" in ElementObj.
   */
  refreshLocalPosition() {
    this.position.copy(
      toVector3(
        CoordinateManager.getInstance().projectToLocal(
          this.projectPosition.x,
          this.projectPosition.y,
        ),
        this.projectPosition.z - CoordinateManager.getInstance().getMAS(),
      ),
    );
  }

  containsHeightData = false;

  private raisedHeight = 7.5;
  private isRaised: boolean;

  min = 0;
  max = 0;

  private model: Group;

  constructor(
    readonly volumeId: string,
    model: GLTF,
  ) {
    super();
    this.model = model.scene;

    model.scene.traverse((node) => {
      if (node instanceof Mesh && node.material instanceof MeshStandardMaterial) {
        node.material.emissive = new Color(0.1, 0.1, 0.1);
        node.material.roughness = 1;
        node.material.metalness = 0;
        node.material.needsUpdate = true;
      }
    });

    this.add(this.model);
    this.isRaised = true;
    this.raised = true;
  }

  private getAttributes(): SkyMapPrismVolumeExtras {
    return this.model.userData as SkyMapPrismVolumeExtras;
  }

  getPrismData(rangeStart: number, maxPrismCount: number): PrismData[] {
    const rangeEnd = rangeStart + maxPrismCount * 3;
    const attributes = this.getAttributes()?.skyMapPrismVolume;
    const cutAndFillData = attributes?.cutAndFillData;
    const areaData = attributes?.area;
    const planAreaData = attributes?.planArea;

    if (!isDefined(cutAndFillData) || !isDefined(areaData) || !isDefined(planAreaData)) {
      throw new Error('Failed getting prism list. No attributes found.');
    }

    const topMesh = this.model.children[0].children[0] as Mesh;
    const bottomMesh = this.model.children[0].children[1] as Mesh;

    // Update matrix world before getting the positions.
    this.updateMatrixWorld(true);

    const positionTop = getPositionArray(topMesh, true);
    const positionBottom = getPositionArray(bottomMesh, true);

    const prismCount = positionTop.length / 3;

    if (
      !(
        positionTop.length === positionBottom.length &&
        cutAndFillData.length === prismCount &&
        areaData.length === prismCount &&
        planAreaData.length === prismCount
      )
    ) {
      throw new Error('Data mismatch. All arrays need to be the same length');
    }

    const prismData: PrismData[] = [];

    for (let i = rangeStart; i < positionTop.length && i < rangeEnd; i += 3) {
      const p1 = CoordinateManager.getInstance().localToProjectCoords(positionTop[i]);
      const p2 = CoordinateManager.getInstance().localToProjectCoords(positionTop[i + 1]);
      const p3 = CoordinateManager.getInstance().localToProjectCoords(positionTop[i + 2]);

      prismData.push([
        p1.northing,
        p1.easting,
        positionTop[i].z - positionBottom[i].z,
        p2.northing,
        p2.easting,
        positionTop[i + 1].z - positionBottom[i + 1].z,
        p3.northing,
        p3.easting,
        positionTop[i + 2].z - positionBottom[i + 2].z,
        cutAndFillData[i / 3],
        areaData[i / 3],
        planAreaData[i / 3],
      ]);
    }

    return prismData;
  }

  dispose() {
    disposeRecursive(this.model);
  }
}

/**
 * [x1,y1,zOffset1,x2,y2,zOffset2,x3,y3,zOffset3,volume,area,planArea]
 */
export type PrismData = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];
